import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Button } from '../ui';
import { EyeIcon, XMarkIcon, UsersIcon } from '@heroicons/react/24/outline';
import { CATEGORIES } from '../ui/CategorySelect';
import { 
  getSubscriptionBadge, 
  getBasicInfoBadge, 
  getAnalysisBadge, 
  getQualityBadge, 
  getTypeBadge, 
  getPrivacyBadge 
} from '../../utils/groupBadges';

const formatThemeLabel = (group) => {
  if (group?.category) {
    return group.category;
  }

  if (group?.theme_id === null || group?.theme_id === undefined || group?.theme_id === '') {
    return 'Uncategorized';
  }

  const normalizedTheme = String(group.theme_id).trim();
  if (/^\d+$/.test(normalizedTheme) && CATEGORIES[normalizedTheme]) {
    return CATEGORIES[normalizedTheme];
  }

  return normalizedTheme;
};

const GroupsTable = ({
  groups,
  selectedGroups,
  onSelectGroup,
  onSelectAll,
  onUnsubscribe,
  onAccountScan,
  isLoading,
  paginationInfo,
  searchTerm,
  hasActiveFilters
}) => {
  const handleSelectAll = (checked) => {
    onSelectAll(checked, groups);
  };

  const allSelected = groups.length > 0 && groups.every(group => selectedGroups.has(group.chat_id));
  const someSelected = groups.some(group => selectedGroups.has(group.chat_id));

  if (groups.length === 0) {
    return (
      <Card>
        <div className="text-center py-6">
          {searchTerm || hasActiveFilters ? (
            <p className="text-gray-500">No groups found matching your criteria.</p>
          ) : (
            <div>
              <p className="text-gray-500 mb-4">No groups have been added yet.</p>
              <Button
                variant="primary"
                icon={UsersIcon}
                onClick={onAccountScan}
                disabled={isLoading.accountScan}
              >
                {isLoading.accountScan ? 'Scanning...' : 'Scan Accounts for Groups'}
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Group
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Type & Privacy
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Basic Info
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Members
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Theme & Geo
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Quality
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                AI Analysis
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Subscription
              </th>
              <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groups.map((group) => (
              <tr key={group.chat_id} className="hover:bg-gray-50">
                <td className="px-2 py-3 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedGroups.has(group.chat_id)}
                    onChange={(e) => onSelectGroup(group.chat_id, e.target.checked)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-3 py-3">
                  <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                    {group.title}
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    ID: {group.chat_id}
                  </div>
                  {group.chat_link && (
                    <div className="text-sm text-gray-500">
                      <a
                        href={group.chat_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-900"
                      >
                        {group.chat_username ? `@${group.chat_username}` : 'View Group'}
                      </a>
                    </div>
                  )}
                  {group.discovery_source && (
                    <div className="text-xs text-gray-400">
                      Source: {group.discovery_source}
                    </div>
                  )}
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  <div className="space-y-1">
                    {getTypeBadge(group.type)}
                    {getPrivacyBadge(group.chat_username)}
                  </div>
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  {getBasicInfoBadge(group.basic_info_status)}
                  {group.basic_info_checked_at && (
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(group.basic_info_checked_at).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="text-sm text-gray-900">
                    {group.member_count ? group.member_count.toLocaleString() : 'Unknown'}
                  </div>
                  {group.post_per_hour && typeof group.post_per_hour === 'number' && (
                    <div className="text-xs text-gray-400">
                      {group.post_per_hour.toFixed(1)} posts/h
                    </div>
                  )}
                  {group.unique_senders_count && typeof group.unique_senders_count === 'number' && (
                    <div className="text-xs text-gray-400">
                      {group.unique_senders_count}/100 senders
                    </div>
                  )}
                </td>
                <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="max-w-40 text-sm text-gray-900 truncate">
                    {formatThemeLabel(group)}
                  </div>
                  <div className="mt-1 space-y-1 text-xs text-gray-500">
                    {group.theme_id && <div>Theme ID: {group.theme_id}</div>}
                    {group.country && <div>Country: {group.country}</div>}
                    {group.language && <div>Language: {group.language}</div>}
                    {group.region && (
                      <div className="max-w-40 truncate" title={group.region}>
                        Region: {group.region}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  {getQualityBadge(group.quality, group.quality_check_status)}
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  {getAnalysisBadge(group.ai_analysis_status)}
                  {group.last_quality_check && (
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(group.last_quality_check).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td className="px-2 py-3 whitespace-nowrap">
                  {getSubscriptionBadge(group.subscription_status)}
                  {group.account_id && (
                    <div className="text-xs text-gray-400 mt-1">
                      Acc: {group.account_id}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  <Link
                    to={`/groups/${group.chat_id}`}
                    className="text-primary-600 hover:text-primary-900"
                  >
                    <EyeIcon className="h-4 w-4 inline" />
                  </Link>
                  {group.subscription_status === 'subscribed' && (
                    <button
                      onClick={() => onUnsubscribe(group.chat_id)}
                      disabled={isLoading.unsubscribe}
                      className="text-red-600 hover:text-red-900"
                      title="Unsubscribe"
                    >
                      <XMarkIcon className="h-4 w-4 inline" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Total count info */}
      {groups.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="text-sm text-gray-700">
            Showing all {groups.length} groups
            {paginationInfo.total && paginationInfo.total !== groups.length && (
              <span className="text-gray-500"> (filtered from {paginationInfo.total} total)</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default GroupsTable;
